<template>
    <div v-if="variant === 'buttons' && (interactive || showRating)" class="buttons-row -mx-5 flex flex-wrap items-center justify-between gap-4 border-t border-border px-5">
        <div v-if="showRating" class="flex items-center gap-2">
            <BaseButton
                size="sm"
                class="text-pretty"
                :theme="displayValue === 1 ? 'secondary' : 'neutral'"
                :aria-pressed="displayValue === 1"
                :disabled="disabled"
                data-testid="insight-feedback-good-to-know"
                @click="handleYes">
                {{ t("insights.feedback.goodToKnow") }}
            </BaseButton>
            <BaseButton
                size="sm"
                class="text-pretty"
                :theme="displayValue === 0 ? 'secondary' : 'neutral'"
                :aria-pressed="displayValue === 0"
                :disabled="disabled"
                data-testid="insight-feedback-not-helpful"
                @click="handleNo">
                {{ t("insights.feedback.notHelpful") }}
            </BaseButton>
        </div>
        <DiveDeeperButton v-if="interactive" size="sm" :loading="diveDeeperLoading" @click="$emit('diveDeeper')" />
    </div>

    <div v-else class="feedback-bar flex flex-col items-start gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4" :class="{ 'is-redesigned': redesign }">
        <div class="flex items-center gap-2">
            <i class="bi bi-stars text-ink-muted text-base" aria-hidden="true" />
            <span class="body-small text-ink text-pretty">{{ t("insights.feedback.wasHelpful") }}</span>
        </div>
        <div class="flex self-end items-center gap-2 sm:self-auto">
            <BaseButton
                size="sm"
                class="text-pretty"
                :theme="displayValue === 1 ? 'secondary' : 'neutral'"
                :disabled="disabled"
                data-testid="insight-feedback-yes"
                @click="handleYes">
                {{ t("insights.feedback.yes") }}
            </BaseButton>
            <BaseButton
                size="sm"
                class="text-pretty"
                :theme="displayValue === 0 ? 'secondary' : 'neutral'"
                :disabled="disabled"
                data-testid="insight-feedback-no"
                @click="handleNo">
                {{ t("insights.feedback.no") }}
            </BaseButton>
        </div>
    </div>

    <BaseDialog v-model:open="showFeedbackModal" :title="pendingValue === 1 ? t('myWork.feedback.helpfulTitle') : t('myWork.feedback.notHelpfulTitle')" @close="handleDismissModal">
        <fieldset class="flex flex-col gap-2">
            <legend class="body-small text-ink-muted pb-2">{{ t("insights.feedback.selectAll") }}</legend>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                <BaseCheckbox
                    v-for="issue in availableIssues"
                    :key="issue"
                    shape="square"
                    :model-value="selectedTags.includes(issue)"
                    class="body-small cursor-pointer text-control-label"
                    @update:model-value="setTagSelected(issue, $event)">
                    {{ tagLabels[issue] }}
                </BaseCheckbox>
            </div>
        </fieldset>
        <BaseTextarea
            v-model:input="comment"
            rows="3"
            :placeholder="t('insights.feedback.commentPlaceholder')"
            :aria-label="t('insights.feedback.commentLabel')"
            disable-autosize
            class="resize-none rounded-md border border-control-border p-3 text-control-text"
            :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />
        <template #footer>
            <BaseButton size="sm" theme="primary" :disabled="(!selectedTags.length && !comment.trim()) || feedbackSubmitting" @click="handleSubmitFeedback">{{
                t("insights.feedback.submit")
            }}</BaseButton>
        </template>
    </BaseDialog>

    <BaseNotification v-if="showError" theme="error" :text="t('insights.feedback.error')" @dismiss="showError = false" />
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { i18n } from "~vue/i18n";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, ref, watch } from "vue";

const { t } = i18n.global;

const POSITIVE_TAGS = ["Felt true", "Showed me something new", "Felt personalized", "Gave me a next step", "Made me think"];
const NEGATIVE_TAGS = ["Too generic", "Got me wrong", "Missed important context", "Too obvious", "Not actionable", "Too vague", "Shouldn't have been generated"];
// The English tag is the value stored with the feedback; only its label follows the UI language.
const tagLabels = computed<Record<string, string>>(() => ({
    "Felt true": t("insights.feedback.tags.feltTrue"),
    "Showed me something new": t("insights.feedback.tags.showedSomethingNew"),
    "Felt personalized": t("insights.feedback.tags.feltPersonalized"),
    "Gave me a next step": t("insights.feedback.tags.gaveNextStep"),
    "Made me think": t("insights.feedback.tags.madeMeThink"),
    "Too generic": t("insights.feedback.tags.tooGeneric"),
    "Got me wrong": t("insights.feedback.tags.gotMeWrong"),
    "Missed important context": t("insights.feedback.tags.missedContext"),
    "Too obvious": t("insights.feedback.tags.tooObvious"),
    "Not actionable": t("insights.feedback.tags.notActionable"),
    "Too vague": t("insights.feedback.tags.tooVague"),
    "Shouldn't have been generated": t("insights.feedback.tags.shouldNotExist"),
}));

/**
 * Whether a rating asks for a reason. Onboarding uses "negative-only": the reason is where the
 * actionable signal is, and a modal on the happy path is friction at the worst moment. Transient
 * surfaces use "never" when a negative rating removes the card immediately.
 */
export type ReasonPrompt = "always" | "negative-only" | "never";

interface Props {
    feedbackValue: number | null;
    feedbackTags: string[];
    feedbackComment: string | null;
    disabled?: boolean;
    feedbackSubmitting?: boolean;
    submitError?: boolean;
    reasonPrompt?: ReasonPrompt;
    redesign?: boolean;
    /** "bar" is the control "Was this insight helpful?" bar; "buttons" is the redesigned inline row. */
    variant?: "bar" | "buttons";
    /** "buttons" variant only: whether to offer the rating choices at all. */
    showRating?: boolean;
    /** "buttons" variant only: whether to offer the Dive Deeper button alongside the rating choices. */
    interactive?: boolean;
    /** "buttons" variant only: loading state for the Dive Deeper button. */
    diveDeeperLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    feedbackSubmitting: false,
    submitError: false,
    reasonPrompt: "always",
    redesign: false,
    variant: "bar",
    showRating: true,
    interactive: true,
    diveDeeperLoading: false,
});

const emit = defineEmits<{
    diveDeeper: [];
    select: [value: 0 | 1];
    submitFeedback: [payload: { tags: string[]; comment: string | null; value: 0 | 1 }];
}>();

const showFeedbackModal = ref(false);
const showError = ref(false);
const pendingValue = ref<0 | 1 | null>(null);
const selectedTags = ref<string[]>([]);
const comment = ref("");
const optimisticValue = ref<0 | 1 | null>(null);

const displayValue = computed(() => optimisticValue.value ?? props.feedbackValue);

// Clear optimistic value once the server response arrives
watch(
    () => props.feedbackValue,
    () => {
        optimisticValue.value = null;
    },
);

watch(
    () => props.submitError,
    (error) => {
        if (error) {
            optimisticValue.value = null;
            showError.value = true;
        }
    },
);

const availableIssues = computed(() => (pendingValue.value === 1 ? POSITIVE_TAGS : NEGATIVE_TAGS));

function setTagSelected(tag: string, selected: boolean | "indeterminate") {
    selectedTags.value = selected === true ? [...new Set([...selectedTags.value, tag])] : selectedTags.value.filter((selectedTag) => selectedTag !== tag);
}

function handleYes() {
    if (props.disabled || props.feedbackSubmitting) {
        return;
    }
    pendingValue.value = 1;
    optimisticValue.value = 1;
    if (props.feedbackValue !== 1) {
        emit("select", 1);
    }
    // "buttons" variant is like/dislike-and-done — no reason modal, regardless of reasonPrompt.
    if (props.variant === "buttons" || props.reasonPrompt !== "always") {
        return;
    }
    selectedTags.value = props.feedbackValue === 1 ? [...props.feedbackTags] : [];
    comment.value = props.feedbackValue === 1 ? (props.feedbackComment ?? "") : "";
    showFeedbackModal.value = true;
}

function handleNo() {
    if (props.disabled || props.feedbackSubmitting) {
        return;
    }
    pendingValue.value = 0;
    optimisticValue.value = 0;
    if (props.feedbackValue !== 0) {
        emit("select", 0);
    }
    // "buttons" variant is like/dislike-and-done — no reason modal, regardless of reasonPrompt.
    if (props.variant === "buttons" || props.reasonPrompt === "never") {
        return;
    }
    selectedTags.value = props.feedbackValue === 0 ? [...props.feedbackTags] : [];
    comment.value = props.feedbackValue === 0 ? (props.feedbackComment ?? "") : "";
    showFeedbackModal.value = true;
}

function handleDismissModal() {
    showFeedbackModal.value = false;
}

function handleSubmitFeedback() {
    if (pendingValue.value === null) {
        return;
    }
    emit("submitFeedback", { tags: selectedTags.value, comment: comment.value.trim() || null, value: pendingValue.value });
    showFeedbackModal.value = false;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .feedback-bar {
        @apply bg-surface-sunken;
    }

    .buttons-row {
        /* 20px, matching the card body's horizontal padding so the row's top inset equals its
           sides; the bottom inset is the body's own 20px bottom padding, so the buttons sit
           vertically centered in the footer. */
        margin-top: 1.25rem;
        padding-top: 1.25rem;
    }
}
</style>
